import { useRef, useState } from 'react';
import {
  Button, Form, message, Popover,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Picker, { EmojiClickData } from 'emoji-picker-react';

import styles from './index.module.scss';
import InputFiles from '../../inputFiles';
import { IconAttach, IconEmogie } from '../../Icon';
import { useAuth } from '../../../../store/auth';
import useAuthModals from '../../../Layout/Menu/useAuthModals';
import {
  CommentCreateBody,
  CommentCreateParams,
} from '../../../../store/comment/create';
import { Comment } from '../../../../store/comment';
import {
  useCommentCreate,
  useCommentUpdate,
} from '../../../../hooks/api/comments';
import { FileData } from '../../../../store/types';
import { FetchSuccess, getMessageInError } from '../../../../hooks/fetch';

export interface FormValues {
  message: string;
  files: string[];
}

interface CommentFormProps {
  createParams: CommentCreateParams;
  updateParams?: { id: string };
  onSuccess: (newComment: Partial<Comment>) => void;
  initialValues?: { message: string; files: FileData[] };
}

const MESSAGE_MAX = 2000;

function CommentForm({
  createParams,
  updateParams,
  onSuccess,
  initialValues,
}: CommentFormProps) {
  const { content, openLoginModal } = useAuthModals();
  const createComment = useCommentCreate(createParams?.parentId);
  const updateComment = useCommentUpdate(updateParams?.id || '');
  const { authorized } = useAuth();
  const [form] = Form.useForm<FormValues>();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [defaultFiles, setDefaultFiles] = useState<FileData[]>(
    initialValues?.files || [],
  );
  const [newFiles, setNewFiles] = useState<FileData[]>([]);
  const [uploadKey, setUploadKey] = useState(Date.now());

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    const value = form.getFieldValue('message') || '';
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    let newValue = value.slice(0, start) + emojiData.emoji + value.slice(end);

    if (newValue.length > MESSAGE_MAX) newValue = newValue.slice(0, MESSAGE_MAX);

    form.setFieldsValue({ message: newValue });

    setTimeout(() => {
      textarea.focus();
      const caretPos = Math.min(start + emojiData.emoji.length, MESSAGE_MAX);

      textarea.setSelectionRange(caretPos, caretPos);
    }, 0);

    setIsPickerOpen(false);
  };

  const handleCreateComment = (values: FormValues) => {
    const { entity, ...initialBody } = createParams.body;

    const safeText = (values.message || '').slice(0, MESSAGE_MAX);

    const commentBody: CommentCreateBody & Record<string, any> = {
      ...initialBody,
      text: safeText,
    };

    if (Array.isArray(values.files) && values.files.length > 0) {
      commentBody.fileIds = values.files;
    }

    if (!createParams.parentId && entity) {
      commentBody.entity = entity;
    }

    createComment
      .fetch(commentBody)
      .then((res: Partial<Comment> | null) => {
        if (!res) return;

        form.resetFields();
        setDefaultFiles([]);
        setUploadKey(Date.now());
        createComment.clearResponse();
        onSuccess(res);
      })
      .catch((error) => {
        message.error(getMessageInError(error)).then(() => {
          createComment.clearError();
        });
      });
  };

  const handleUpdateComment = (values: FormValues) => {
    const safeText = (values.message || '').slice(0, MESSAGE_MAX);

    const commentBody = {
      text: safeText,
      fileIds: values.files,
    };

    updateComment
      .fetch(commentBody)
      .then((data: FetchSuccess | null) => {
        if (!data) return;

        updateComment.clearResponse();
        const files = [
          ...(initialValues?.files || []).filter(({ id }) => values.files?.includes(id)),
          ...newFiles,
        ];

        onSuccess({
          id: updateParams?.id || '',
          text: safeText,
          files,
        });

        form.resetFields();
        setDefaultFiles([]);
        setNewFiles([]);
      })
      .catch((error) => {
        message.error(getMessageInError(error)).then(() => {
          updateComment.clearError();
        });
      });
  };

  const onFinish = (values: FormValues) => {
    if (!authorized) {
      openLoginModal();

      return;
    }

    if (updateParams) {
      handleUpdateComment(values);
    } else {
      handleCreateComment(values);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Form<FormValues>
        form={form}
        autoComplete="off"
        layout="vertical"
        onFinish={onFinish}
        className={styles.form}
        initialValues={{ message: '', files: [], ...(initialValues || {}) }}
      >
        <div className={styles.inputsWrapper}>
          <Form.Item
            name="message"
            rules={[
              { required: true, message: 'Please enter a message' },
              {
                max: MESSAGE_MAX,
                message: `Message must be at most ${MESSAGE_MAX} characters`,
              },
            ]}
            className={styles.inputFormItem}
          >
            <TextArea
              className={styles.input}
              size="large"
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder="Enter your comment"
              maxLength={MESSAGE_MAX}
              showCount
              ref={(node) => {
                if (node && textareaRef.current !== node) {
                  textareaRef.current = node?.resizableTextArea?.textArea || null;
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  form.submit();
                }
              }}
            />
          </Form.Item>

          <Popover
            content={<Picker onEmojiClick={handleEmojiSelect} />}
            trigger="click"
            open={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            placement="topRight"
          >
            <div className={styles.emogieWrapper} style={{ cursor: 'pointer' }}>
              <IconEmogie className={styles.emogie} />
            </div>
          </Popover>
        </div>

        <div className={styles.controllsWrapper}>
          <InputFiles
            key={uploadKey}
            form={form}
            name="files"
            uploadBtn={<IconAttach />}
            className={styles.file}
            rules={[{ required: false }]}
            disabled={!authorized}
            defaultFiles={defaultFiles}
            onFileDataChange={setNewFiles}
          />

          <Button
            type="text"
            className={styles.submit}
            onClick={form.submit}
            loading={createComment.loading || updateComment.loading}
          >
            Submit
          </Button>
          {content}
        </div>
      </Form>
    </div>
  );
}

export default CommentForm;
